/**
 * Progressive load plan (runtime-handoff-v5.2, anime-stadium export).
 *
 * Stage 1 (gameplay-critical) must be ready before the player enters: core, pitch,
 * goals, collision + markers.json. Stage 2 (roof, seats, props, crowd, staff, and
 * the bowl-dressing modules: goalnets, ads, scoreboard, very-big flags) streams in
 * afterwards and must NOT block entry. Exterior plaza/trees are optional — the
 * README for this export documents them as never on-camera from any pitch-level
 * view, so they are quality-gated rather than a hard secondary dependency.
 *
 * `tunnel_dugouts` and `grass_card_near` from the previous asset generation no
 * longer exist as separate files: tunnels are now merged into `core`, dugouts into
 * `props` (see the export's own per-file triangle table).
 *
 * Loading progress is BYTE-based (downloaded / total), never fake timing (§3), and
 * all modular GLBs share the world origin — no per-part reposition (§4), so this
 * plan carries no correction offsets.
 */
export type LoadStage = 'critical' | 'secondary' | 'optional';

export interface LoadAsset {
  /** manifest.assets key (or a data key). */
  key: string;
  /** Path relative to the handoff root. */
  path: string;
  stage: LoadStage;
  kind: 'glb' | 'json';
  /** Approx bytes (from the manifest/export report) for byte-based progress before
   *  Content-Length is known; the loader replaces it with the real header value. */
  approxBytes: number;
}

/** The canonical plan. `approxBytes` are the measured sizes of the files actually
 *  shipped in `public/assets/stadium-v5/`. */
export const LOAD_PLAN: LoadAsset[] = [
  // ── Stage 1: gameplay-critical — the player can enter once these resolve. ──
  { key: 'core', path: 'models/stadium_core.glb', stage: 'critical', kind: 'glb', approxBytes: 314_112 },
  { key: 'pitch', path: 'models/stadium_pitch.glb', stage: 'critical', kind: 'glb', approxBytes: 90_664 },
  { key: 'goals', path: 'models/stadium_goals.glb', stage: 'critical', kind: 'glb', approxBytes: 4_580 },
  { key: 'collision', path: 'models/stadium_collision.glb', stage: 'critical', kind: 'glb', approxBytes: 13_948 },
  { key: 'markers', path: 'data/markers.json', stage: 'critical', kind: 'json', approxBytes: 3_768 },
  // ── Stage 2: secondary — streamed in, never blocks entry. ──
  { key: 'roof', path: 'models/stadium_roof.glb', stage: 'secondary', kind: 'glb', approxBytes: 194_896 },
  { key: 'seats', path: 'models/stadium_seats.glb', stage: 'secondary', kind: 'glb', approxBytes: 225_116 },
  { key: 'props', path: 'models/stadium_props.glb', stage: 'secondary', kind: 'glb', approxBytes: 29_568 },
  { key: 'crowdPlacements', path: 'data/crowd_placements.json', stage: 'secondary', kind: 'json', approxBytes: 2_461_788 },
  { key: 'staffPlacements', path: 'data/staff_placements.json', stage: 'secondary', kind: 'json', approxBytes: 30_582 },
  // ── Stage 2: bowl dressing — new this export, purely decorative, non-blocking. ──
  { key: 'goalnets', path: 'models/stadium_goalnets.glb', stage: 'secondary', kind: 'glb', approxBytes: 7_332 },
  { key: 'ads', path: 'models/stadium_ads.glb', stage: 'secondary', kind: 'glb', approxBytes: 12_088 },
  { key: 'scoreboard', path: 'models/stadium_scoreboard.glb', stage: 'secondary', kind: 'glb', approxBytes: 6_828 },
  { key: 'flagsVeryBig', path: 'models/stadium_flags_verybig.glb', stage: 'secondary', kind: 'glb', approxBytes: 4_228 },
  // ── Optional: quality-gated, documented as never visible from a pitch-level camera. ──
  { key: 'exterior', path: 'models/stadium_exterior.glb', stage: 'optional', kind: 'glb', approxBytes: 25_760 },
];

export function assetsForStage(stage: LoadStage): LoadAsset[] {
  return LOAD_PLAN.filter((a) => a.stage === stage);
}

/** Bytes that must download before the player can enter (stage 1 only). */
export function criticalBytes(): number {
  return assetsForStage('critical').reduce((s, a) => s + a.approxBytes, 0);
}

/** Byte-based progress across a set of assets given per-key downloaded bytes. */
export function byteProgress(assets: readonly LoadAsset[], downloaded: Map<string, number>): number {
  let got = 0;
  let total = 0;
  for (const a of assets) {
    total += a.approxBytes;
    got += Math.min(a.approxBytes, downloaded.get(a.key) ?? 0);
  }
  return total === 0 ? 1 : got / total;
}

/** True once every critical asset has fully downloaded — the "can enter" gate. */
export function canEnterMatch(downloaded: Map<string, number>): boolean {
  return assetsForStage('critical').every((a) => (downloaded.get(a.key) ?? 0) >= a.approxBytes);
}
